import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { NetworkStatus } from "@/components/pwa/NetworkStatus";
import { registerSW } from "virtual:pwa-register";

// Eagerly loaded routes (critical for initial render)
import Index from "./pages/Index";
import AuthPage from "./pages/Auth";

// Lazy loaded routes
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PlayersPage = lazy(() => import("./pages/Players"));
const PlayerProfilePage = lazy(() => import("./pages/PlayerProfile"));
const TournamentsPage = lazy(() => import("./pages/Tournaments"));
const TournamentsHub = lazy(() => import("./pages/TournamentsHub"));
const LeaderboardPage = lazy(() => import("./pages/Leaderboard"));
const SquadsPage = lazy(() => import("./pages/Squads"));
const MatchesPage = lazy(() => import("./pages/Matches"));
const TermsPage = lazy(() => import("./pages/Terms"));
const PrivacyPage = lazy(() => import("./pages/Privacy"));
const ContactPage = lazy(() => import("./pages/Contact"));
const ExternalMatchesPublic = lazy(() => import("./pages/ExternalMatchesPublic"));
const ExternalMatchDetail = lazy(() => import("./pages/ExternalMatchDetail"));
const MatchDetailPage = lazy(() => import("./pages/MatchDetail"));
const NewsPage = lazy(() => import("./pages/News"));
const NewsDetail = lazy(() => import("./pages/NewsDetail"));
const ClubStatsPage = lazy(() => import("./pages/ClubStats"));
const ClubRecordsPage = lazy(() => import("./pages/ClubRecords"));
const DivisionPage = lazy(() => import("./pages/Division"));
const SeasonHistoryPage = lazy(() => import("./pages/SeasonHistory"));
const WorldCup = lazy(() => import("./pages/WorldCup"));
const WorldCupHub = lazy(() => import("./pages/WorldCupHub"));
const WorldCupTournament = lazy(() => import("./pages/WorldCupTournament"));
const WorldCupRegister = lazy(() => import("./pages/WorldCupRegister"));
const MaintenancePage = lazy(() => import("./pages/Maintenance"));
const LiveMatchView = lazy(() => import("./pages/LiveMatchView"));
const LiveMatches = lazy(() => import("./pages/LiveMatches"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin routes (lazy loaded)
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminPlayers = lazy(() => import("./pages/admin/Players"));
const AdminTournaments = lazy(() => import("./pages/admin/Tournaments"));
const AdminTournamentSystems = lazy(() => import("./pages/admin/TournamentSystems"));
const AdminMatches = lazy(() => import("./pages/admin/Matches"));
const AdminMatchManage = lazy(() => import("./pages/admin/MatchManage"));
const AdminSquads = lazy(() => import("./pages/admin/Squads"));
const AdminDraw = lazy(() => import("./pages/admin/Draw"));
const AdminLeaderboard = lazy(() => import("./pages/admin/Leaderboard"));
const AdminExternalMatches = lazy(() => import("./pages/admin/ExternalMatches"));
const AdminImportExport = lazy(() => import("./pages/admin/ImportExport"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AuditLogs"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminResultSubmit = lazy(() => import("./pages/admin/ResultSubmit"));
const AdminSeasons = lazy(() => import("./pages/admin/Seasons"));
const AdminPasswordRequests = lazy(() => import("./pages/admin/PasswordRequests"));
const AdminDivisionResults = lazy(() => import("./pages/admin/DivisionResults"));
const HeadAdminDashboard = lazy(() => import("./pages/admin/HeadAdminDashboard"));
const WorldCupManage = lazy(() => import("./pages/admin/WorldCupManage"));
const WorldCupList = lazy(() => import("./pages/admin/WorldCupList"));
const WCDashboard = lazy(() => import("./pages/admin/worldcup/WCDashboard"));
const WCCreate = lazy(() => import("./pages/admin/worldcup/WCCreate"));
const WCManage = lazy(() => import("./pages/admin/worldcup/WCManage"));
const CardGenerator = lazy(() => import("./pages/admin/CardGenerator"));
const MOTMCardGenerator = lazy(() => import("./pages/admin/MOTMCardGenerator"));
const PlayerPerformanceCard = lazy(() => import("./pages/admin/PlayerPerformanceCard"));
const EmailCenter = lazy(() => import("./pages/admin/EmailCenter"));
const SubmissionReports = lazy(() => import("./pages/admin/SubmissionReports"));
const CardsAndDiscipline = lazy(() => import("./pages/admin/CardsAndDiscipline"));
const DivisionMatchSchedule = lazy(() => import("./pages/admin/DivisionMatchSchedule"));
const TrophyManagement = lazy(() => import("./pages/admin/TrophyManagement"));
const LiveMatchControl = lazy(() => import("./pages/admin/LiveMatchControl"));
const LiveDashboard = lazy(() => import("./pages/admin/LiveDashboard"));
const NewsManagement = lazy(() => import("./pages/admin/NewsManagement"));
const PlayerActivity = lazy(() => import("./pages/admin/PlayerActivity"));
const ManualFixture = lazy(() => import("./pages/admin/ManualFixture"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Use cached data while fetching fresh data
      staleTime: 1000 * 60 * 2, // 2 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Register service worker with auto-update
const updateSW = registerSW({
  onNeedRefresh() {
    // Auto-update when new content is available
    if (confirm('নতুন আপডেট পাওয়া গেছে। রিফ্রেশ করতে চান?')) {
      updateSW(true);
    }
  },
  onOfflineReady() {
    console.log('App ready for offline use');
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider>
            <NetworkStatus />
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/players" element={<PlayersPage />} />
                  <Route path="/players/:slug" element={<PlayerProfilePage />} />
                  <Route path="/tournaments" element={<TournamentsHub />} />
                  <Route path="/tournaments-old" element={<TournamentsPage />} />
                  <Route path="/leaderboard" element={<LeaderboardPage />} />
                  <Route path="/squads" element={<SquadsPage />} />
                  <Route path="/matches" element={<MatchesPage />} />
                  <Route path="/matches/:id" element={<MatchDetailPage />} />
                  <Route path="/news" element={<NewsPage />} />
                  <Route path="/news/:newsId" element={<NewsDetail />} />
                  <Route path="/club-stats" element={<ClubStatsPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/external-matches" element={<ExternalMatchesPublic />} />
                  <Route path="/external-matches/:id" element={<ExternalMatchDetail />} />
                  <Route path="/club-records" element={<ClubRecordsPage />} />
                  <Route path="/seasons" element={<Navigate to="/division" replace />} />
                  <Route path="/division" element={<DivisionPage />} />
                  <Route path="/season-history" element={<SeasonHistoryPage />} />
                  <Route path="/world-cup" element={<WorldCupHub />} />
                  <Route path="/world-cup/:id" element={<WorldCupTournament />} />
                  <Route path="/world-cup/:id/register" element={<WorldCupRegister />} />
                  <Route path="/world-cup-old/:id" element={<WorldCup />} />
                  <Route path="/live" element={<LiveMatches />} />
                  <Route path="/live/:id" element={<LiveMatchView />} />
                  <Route path="/maintenance" element={<MaintenancePage />} />
                  
                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/players" element={<AdminPlayers />} />
                  <Route path="/admin/tournament-systems" element={<AdminTournamentSystems />} />
                  <Route path="/admin/tournaments" element={<AdminTournaments />} />
                  <Route path="/admin/matches" element={<AdminMatches />} />
                  <Route path="/admin/matches/:id" element={<AdminMatchManage />} />
                  <Route path="/admin/squads" element={<AdminSquads />} />
                  <Route path="/admin/draw" element={<AdminDraw />} />
                  <Route path="/admin/leaderboard" element={<AdminLeaderboard />} />
                  <Route path="/admin/result-submit" element={<AdminResultSubmit />} />
                  <Route path="/admin/external-matches" element={<AdminExternalMatches />} />
                  <Route path="/admin/import-export" element={<AdminImportExport />} />
                  <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                  <Route path="/admin/seasons" element={<AdminSeasons />} />
                  <Route path="/admin/password-requests" element={<AdminPasswordRequests />} />
                  <Route path="/admin/division-results" element={<AdminDivisionResults />} />
                  <Route path="/admin/head-admin" element={<HeadAdminDashboard />} />
                  <Route path="/admin/world-cup" element={<WCDashboard />} />
                  <Route path="/admin/world-cup/create" element={<WCCreate />} />
                  <Route path="/admin/world-cup/:id" element={<WCManage />} />
                  <Route path="/admin/world-cup-old" element={<WorldCupList />} />
                  <Route path="/admin/world-cup-old/:id" element={<WorldCupManage />} />
                  <Route path="/admin/card-generator" element={<CardGenerator />} />
                  <Route path="/admin/motm-card" element={<MOTMCardGenerator />} />
                  <Route path="/admin/performance-cards" element={<PlayerPerformanceCard />} />
                  <Route path="/admin/email-center" element={<EmailCenter />} />
                  <Route path="/admin/reports" element={<SubmissionReports />} />
                  <Route path="/admin/cards" element={<CardsAndDiscipline />} />
                  <Route path="/admin/division-schedule" element={<DivisionMatchSchedule />} />
                  <Route path="/admin/trophies" element={<TrophyManagement />} />
                  <Route path="/admin/live" element={<LiveDashboard />} />
                  <Route path="/admin/live/:id" element={<LiveMatchControl />} />
                  <Route path="/admin/news" element={<NewsManagement />} />
                  <Route path="/admin/player-activity" element={<PlayerActivity />} />
                  <Route path="/admin/manual-fixture" element={<ManualFixture />} />
                  
                  
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
